import { baseApi } from "@/app/store/api/baseApi";

export interface ModulePermissionCaps {
  module: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_assign: boolean;
  can_approve: boolean;
  can_export: boolean;
  can_view_pricing?: boolean;
  can_see_clients?: boolean;
  /**
   * Whose records `can_view` covers. Capabilities say which verbs; scope
   * says whose rows. Only scheduling consumes it today - everything else
   * resolves to "all", which is how the app already behaved.
   */
  scope?: "own" | "all";
  source?: "role_default" | "override";
}

export type MyPermissionsResponse =
  | { static_role: true; full_access: true }
  | Record<string, ModulePermissionCaps>;

/** Which capabilities each module actually enforces, keyed by module. */
export type ModuleCapabilities = Record<string, string[]>;

export const permissionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMyPermissions: builder.query<MyPermissionsResponse, void>({
      query: () => "/manifest/permissions/mine/",
      providesTags: ["MyPermissions"],
    }),

    /** GET /manifest/permissions/capabilities/
     *
     *  The matrix renders 19 modules x 7 capabilities, and most combinations
     *  are not implemented. A tick box that changes nothing is worse than no
     *  box: an administrator ticks it, believes access was granted or
     *  withheld, and is wrong.
     *
     *  The server derives this from the endpoints and has a test that fails
     *  when the two drift. Asking it beats keeping a second copy here, which
     *  is what the hardcoded lists in permissionModuleMap were - and they had
     *  already drifted in both directions. */
    getModuleCapabilities: builder.query<ModuleCapabilities, void>({
      query: () => "/manifest/permissions/capabilities/",
    }),
  }),
});

export const { useGetMyPermissionsQuery, useGetModuleCapabilitiesQuery } =
  permissionsApi;
